/**
 * Client-side utilities for HubSpot deal stage to unit status mapping
 * This mirrors the mapping logic in the webhook Edge Function
 */

/**
 * Map HubSpot deal stage to unit availability status
 * This is the reverse mapping of mapUnitStatusToDealStage in hubspot-deals.ts
 * Based on Investor Sales Pipeline (portal 50726975)
 * 
 * Note: This mapping should match the webhook Edge Function mapping
 */
export function mapDealStageToUnitStatus(dealStage: string): 'available' | 'reserved' | 'sold' | null {
  // HubSpot deal stages can be stage IDs or stage names
  const stageMap: Record<string, 'available' | 'reserved' | 'sold'> = {
    // Stage IDs (from Investor Sales Pipeline - portal 50726975)
    '1225024819': 'available',  // New Lead
    '1225024820': 'available',  // Investment Consultation
    '1225024821': 'available',  // Property Tour
    '1225024822': 'reserved',   // Proposal Submitted (triggers Reserved)
    '1225024823': 'reserved',   // Deal Negotiation
    '1227522179': 'available',  // Qualified To Buy
    '1225024824': 'reserved',  // Agreement Signed
    'closedwon': 'sold',        // Closed Won
    'closedlost': 'available',  // Closed Lost - unit becomes available again
    
    // Stage names (fallback)
    'newlead': 'available',
    'investmentconsultation': 'available',
    'propertytour': 'available',
    'proposalsubmitted': 'reserved',
    'dealnegotiation': 'reserved',
    'qualifiedtobuy': 'available',
    'agreementsigned': 'reserved',
    'appointmentscheduled': 'available',
    'presentationscheduled': 'available',
    'decisionmakerboughtin': 'reserved',
    'contractsent': 'reserved',
  };
  
  return stageMap[dealStage.toLowerCase()] || null;
}

/**
 * Get human-readable status name from deal stage
 */
export function getStatusNameFromDealStage(dealStage: string): string {
  const status = mapDealStageToUnitStatus(dealStage);
  if (!status) return 'Unknown';
  
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * Map deal stage ID to human-readable stage name
 * Based on Investor Sales Pipeline (portal 50726975)
 */
export function getDealStageName(stageId: string): string {
  const stageNames: Record<string, string> = {
    '1225024819': 'New Lead',
    '1225024820': 'Investment Consultation',
    '1225024821': 'Property Tour',
    '1225024822': 'Proposal Submitted',
    '1225024823': 'Deal Negotiation',
    '1227522179': 'Qualified To Buy',
    '1225024824': 'Agreement Signed',
    'closedwon': 'Closed Won',
    'closedlost': 'Closed Lost',
  };
  
  // Return readable name if found, otherwise return the ID
  return stageNames[stageId] || stageId;
}

/**
 * Deal stage configuration for Investor Sales Pipeline (portal 50726975)
 * Used for admin panel deal stage selection
 */
export interface DealStageOption {
  id: string;
  name: string;
  status: 'available' | 'reserved' | 'sold';
}

export const DEAL_STAGES: DealStageOption[] = [
  { id: '1225024819', name: 'New Lead', status: 'available' },
  { id: '1225024820', name: 'Investment Consultation', status: 'available' },
  { id: '1225024821', name: 'Property Tour', status: 'available' },
  { id: '1225024822', name: 'Proposal Submitted', status: 'reserved' },
  { id: '1225024823', name: 'Deal Negotiation', status: 'reserved' },
  { id: '1227522179', name: 'Qualified To Buy', status: 'available' },
  { id: '1225024824', name: 'Agreement Signed', status: 'reserved' },
  { id: 'closedwon', name: 'Closed Won', status: 'sold' },
  { id: 'closedlost', name: 'Closed Lost', status: 'available' },
];

/**
 * Get deal stage option by ID
 */
export function getDealStageOption(stageId: string): DealStageOption | undefined {
  return DEAL_STAGES.find(stage => stage.id === stageId);
}

/**
 * Map deal stage ID to availability status
 * This is a convenience function that uses the DEAL_STAGES array
 */
export function mapDealStageIdToStatus(stageId: string): 'available' | 'reserved' | 'sold' | null {
  const stage = getDealStageOption(stageId);
  return stage ? stage.status : null;
}

/**
 * Get badge color classes for deal stage based on its mapped status
 * Returns Tailwind CSS classes consistent with availability badge styling
 */
export function getDealStageBadgeClasses(stageId: string | null): string {
  if (!stageId) {
    return 'bg-gray-500 hover:bg-gray-600 text-white border-gray-600 whitespace-nowrap';
  }
  
  const status = mapDealStageIdToStatus(stageId);
  
  switch (status) {
    case 'available':
      return 'bg-status-available hover:bg-status-available/90 text-white border-status-available whitespace-nowrap';
    case 'reserved':
      return 'bg-status-reserved hover:bg-status-reserved/90 text-white border-status-reserved whitespace-nowrap';
    case 'sold':
      return 'bg-status-sold hover:bg-status-sold/90 text-white border-status-sold whitespace-nowrap';
    default:
      return 'bg-gray-500 hover:bg-gray-600 text-white border-gray-600 whitespace-nowrap';
  }
}

